import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Download, Trash2, ChevronRight, LogOut, Loader2, AlertTriangle, X, FileText, Database, Users, Flag } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import ReportSheet from "./ReportSheet";

export default function PrivacySection() {
  const navigate = useNavigate();
  const { logout, setUser, user } = useAuth();
  const [exporting, setExporting] = useState(false);
  const [dumping, setDumping] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [listing, setListing] = useState(false);
  const downloadTesters = async () => {
    setListing(true);
    try {
      const { data } = await api.get("/admin/testers.csv", { responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(data); a.download = "sorted-testers.csv"; a.click(); URL.revokeObjectURL(a.href);
      toast.success("Tester list downloading");
    } catch (err) { toast.error("Couldn't fetch the tester list"); }
    finally { setListing(false); }
  };
  const exportAll = async () => {
    setDumping(true);
    try {
      const { data, headers } = await api.get("/admin/export", { responseType: "blob" });
      const name = /filename="([^"]+)"/.exec(headers["content-disposition"] || "")?.[1] || "sorted-db.json";
      const a = document.createElement("a"); a.href = URL.createObjectURL(data); a.download = name; a.click(); URL.revokeObjectURL(a.href);
      toast.success("Full database export downloading");
    } catch (err) { toast.error("Couldn't export the database"); }
    finally { setDumping(false); }
  };
  const [confirm, setConfirm] = useState(false);
  const [typed, setTyped] = useState("");
  const [deleting, setDeleting] = useState(false);

  const exportData = async () => {
    setExporting(true);
    try {
      const { data } = await api.get("/account/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "sorted-my-data.json"; a.click(); URL.revokeObjectURL(a.href);
      toast.success("Your data is downloading");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setExporting(false); }
  };

  const del = async () => {
    setDeleting(true);
    try {
      await api.delete("/account", { data: { confirm: typed } });
      localStorage.clear();
      setUser(false);
      toast.success("Your account is gone. Take care — Millie x");
      navigate("/login", { replace: true });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); setDeleting(false); }
  };

  return (
    <div className="px-6 mt-8" data-testid="privacy-section">
      <h2 className="font-heading text-lg font-semibold text-sorted-ink flex items-center gap-2 mb-3"><ShieldCheck size={18} strokeWidth={2.5} className="text-emerald-600" /> Privacy &amp; your data</h2>
      <div className="bg-white rounded-3xl border-2 border-[#FDECEF] shadow-soft divide-y divide-[#FDECEF]">
        <p className="px-4 py-3 text-xs font-semibold text-sorted-slate">Your household, plans and photos are used only to plan your dinners. Never sold, never used to train AI.</p>
        <Row icon={ShieldCheck} label="Privacy policy" desc="How Millie handles your data, in plain English" onClick={() => navigate("/privacy")} testId="privacy-link" />
        <Row icon={FileText} label="Terms of service" desc="The deal between you and Sorted" onClick={() => navigate("/terms")} testId="terms-link" />
        <Row icon={exporting ? Loader2 : Download} spin={exporting} label="Download my data" desc="Everything we hold, as a file" onClick={exportData} testId="export-data" />
        <Row icon={Flag} label="Report a problem" desc="Something broken, or Millie got it wrong? Tell Nikki" onClick={() => setReportOpen(true)} testId="report-problem" />
        {user?.role === "admin" && <Row icon={listing ? Loader2 : Users} spin={listing} label="Download tester list" desc="Admin only · name & email of every user as CSV for Google Play" onClick={downloadTesters} testId="admin-testers-csv" />}
        {user?.role === "admin" && <Row icon={dumping ? Loader2 : Database} spin={dumping} label="Export whole database" desc="Admin only · every collection as one JSON file" onClick={exportAll} testId="admin-export-db" />}
        <Row icon={LogOut} label="Log out" desc="Sign out on this device" onClick={logout} testId="logout" />
        <Row icon={Trash2} label="Delete my account" desc="Permanently removes you and every household you own" onClick={() => setConfirm(true)} testId="delete-account" danger />
      </div>

      {confirm && (
        <div className="fixed inset-0 z-[80] bg-sorted-ink/50 backdrop-blur-sm flex items-end justify-center" onClick={() => setConfirm(false)} data-testid="delete-modal">
          <div className="w-full max-w-md bg-[#FFF6F1] rounded-t-[2rem] p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3">
              <span className="w-11 h-11 rounded-2xl bg-red-100 text-red-600 flex items-center justify-center shrink-0"><AlertTriangle size={20} strokeWidth={2.5} /></span>
              <div className="flex-1">
                <p className="font-heading font-semibold text-sorted-ink leading-tight">Delete your Sorted account?</p>
                <p className="text-xs text-sorted-slate font-semibold mt-1">This wipes your profile, every household you own, all meal plans, memories, photos, wins and shopping lists. People you've shared with lose access. There's no undo.</p>
              </div>
              <button onClick={() => setConfirm(false)} aria-label="Close" data-testid="delete-close" className="w-9 h-9 rounded-full bg-white flex items-center justify-center text-sorted-slate"><X size={16} strokeWidth={3} /></button>
            </div>
            <p className="text-xs font-bold text-sorted-ink mt-4 mb-1.5">Type <span className="font-mono bg-white px-1.5 py-0.5 rounded">DELETE</span> to confirm</p>
            <input value={typed} onChange={(e) => setTyped(e.target.value)} data-testid="delete-confirm-input" autoCapitalize="characters" placeholder="DELETE"
              className="w-full bg-white rounded-2xl px-4 py-3 font-semibold text-sorted-ink outline-none focus:ring-2 focus:ring-red-200 border-2 border-[#FDECEF]" />
            <button onClick={del} disabled={typed.trim().toUpperCase() !== "DELETE" || deleting} data-testid="delete-confirm-button"
              className="mt-4 w-full bg-red-600 text-white font-bold py-4 rounded-full flex items-center justify-center gap-2 disabled:opacity-40">
              {deleting ? <Loader2 size={17} className="animate-spin" /> : <Trash2 size={17} strokeWidth={2.5} />} Delete everything
            </button>
            <button onClick={exportData} data-testid="delete-export-first" className="mt-2 w-full text-[11px] font-bold text-sorted-slate">Download my data first</button>
          </div>
        </div>
      )}
      <ReportSheet open={reportOpen} onClose={() => setReportOpen(false)} />
    </div>
  );
}

function Row({ icon: Icon, label, desc, onClick, testId, danger, spin }) {
  return (
    <button onClick={onClick} data-testid={testId} className="w-full px-4 py-3.5 flex items-center gap-3 text-left">
      <span className={`w-9 h-9 rounded-2xl flex items-center justify-center ${danger ? "bg-red-50 text-red-600" : "bg-muted text-sorted-ink"}`}><Icon size={17} strokeWidth={2.5} className={spin ? "animate-spin" : ""} /></span>
      <span className="flex-1">
        <span className={`font-heading font-semibold block ${danger ? "text-red-600" : "text-sorted-ink"}`}>{label}</span>
        <span className="text-xs font-semibold text-sorted-slate">{desc}</span>
      </span>
      <ChevronRight size={16} className="text-sorted-slate" />
    </button>
  );
}
